import { apiClient } from '@borradh-workspace/api-client';
import type { WebsiteAnalysisSnapshot } from '@borradh-workspace/api-client/types';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

interface UseUploadAnalysisSnapshotOptions {
  onSuccess?: (result: WebsiteAnalysisSnapshot) => void;
  onError?: (error: Error) => void;
}

/**
 * Upload an off-app analysis snapshot (produced by an operator with Claude
 * Code) and resolve with the validated snapshot in the SAME shape a live scan
 * returns.
 *
 * The onboarding form's existing scan-completion mapping consumes this shape
 * — services, brand, hours, locations, practitioners — so this hook is the
 * "operator upload replaces the scanner" seam without a second mapping code
 * path.
 *
 * Backed by `POST /website-analysis/validate-snapshot`, which is synchronous
 * because a validation costs milliseconds (no scrape, no LLM) and there is
 * nothing to poll.
 */
export const useUploadAnalysisSnapshot = (
  options?: UseUploadAnalysisSnapshotOptions
) => {
  const mutation = useMutation({
    mutationFn: async (file: File): Promise<WebsiteAnalysisSnapshot> => {
      const text = await file.text();
      let snapshot: unknown;
      try {
        snapshot = JSON.parse(text);
      } catch {
        // Fail-fast BEFORE the round-trip. A malformed JSON on the wire would
        // 400 anyway, but the operator gets a clearer message this way — and
        // it avoids a request the server can never satisfy.
        throw new Error(
          `${file.name} is not valid JSON. Re-run the Claude Code brief and try again.`
        );
      }
      return apiClient.post<WebsiteAnalysisSnapshot>(
        'website-analysis/validate-snapshot',
        { snapshot }
      );
    },
    onSuccess: (result) => {
      toast.success('Analysis snapshot loaded');
      options?.onSuccess?.(result);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to load the analysis snapshot');
      options?.onError?.(error);
    },
  });

  return {
    uploadSnapshot: mutation.mutate,
    uploadSnapshotAsync: mutation.mutateAsync,
    isUploading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    data: mutation.data,
    reset: mutation.reset,
  };
};
